/**
 * `useSelectionHighlight` — App-shell-level hook that paints a custom
 * CSS Highlight covering the active selection range in
 * `useSelectionContextStore`. Mounted once at the App shell next to
 * `useSelectionCapture`.
 *
 * **Why a custom highlight?** When the lawyer selects text in a
 * viewer + then clicks into the composer textarea to type a prompt,
 * the browser's native `::selection` rendering disappears (the
 * document selection is replaced by the textarea's caret). The
 * chip below the composer keeps reflecting the selection state
 * (operator brief 2026-06-28 — 5th refinement), but the lawyer
 * loses the visual anchor in the viewer mid-prompt — they don't
 * remember exactly what they selected.
 *
 * The CSS Custom Highlight API (`CSS.highlights` + the `::highlight()`
 * pseudo) decouples our visible highlight from the browser's
 * selection state — we own the range, the browser paints our color
 * regardless of where focus lives. The matching `::highlight()` CSS
 * rule lives in `src/index.css`.
 *
 * **Browser support.** Chrome 105+ / Edge 105+ / Safari 17.2+ / Firefox
 * 140+. The hook feature-detects (`typeof Highlight`) and is a
 * silent no-op in older browsers — the chip + invisible-context
 * directive still work; only the persistent in-viewer highlight is
 * absent. No fallback shim (operator brief: don't ship dead-code
 * fallbacks).
 */

import { useEffect } from "react";
import { useSelectionContextStore } from "@/stores/useSelectionContextStore";

const HIGHLIGHT_NAME = "lexiun-selection";

export function useSelectionHighlight() {
  useEffect(() => {
    // Feature-detect — older browsers silently no-op.
    if (
      typeof Highlight === "undefined" ||
      typeof CSS === "undefined" ||
      !CSS.highlights
    ) {
      return;
    }

    function paint(range: Range | null) {
      if (range) {
        CSS.highlights!.set(HIGHLIGHT_NAME, new Highlight(range));
      } else {
        CSS.highlights!.delete(HIGHLIGHT_NAME);
      }
    }

    // Paint current state on mount (handles HMR / re-mount).
    paint(useSelectionContextStore.getState().selection?.range ?? null);

    // Subscribe to store changes.
    const unsub = useSelectionContextStore.subscribe((state) => {
      paint(state.selection?.range ?? null);
    });

    return () => {
      unsub();
      CSS.highlights!.delete(HIGHLIGHT_NAME);
    };
  }, []);
}
